import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaUser, FaUpload } from 'react-icons/fa';

const AddUserModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    status: true,
    avatar: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // handle text inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // handle image upload & preview (convert to Base64)
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, avatar: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // submit handler
  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    onSubmit(formData); // send data to parent
    setFormData({
      name: '',
      role: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      status: true,
      avatar: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* --- HEADER --- */}
        <div className="modal-header">
          <h2 className="modal-title">{t('admin.user_management.add_user')}</h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* --- IMAGE UPLOAD --- */}
        <div className="image-upload">
          <div className="upload-placeholder">
            {formData.avatar ? (
              <img
                src={formData.avatar}
                alt="avatar"
                className="avatar-preview"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #ddd'
                }}
              />
            ) : (
              <FaUser style={{ fontSize: '48px', color: '#9ca3af' }} />
            )}
          </div>

          <label className="upload-btn">
            <FaUpload style={{ marginRight: 8 }} />
            {t('admin.user_management.change_image')}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              hidden
            />
          </label>
          <p className="upload-info">{t('admin.user_management.image_info')}</p>
        </div>

        {/* --- FORM --- */}
        <form onSubmit={handleSubmit}>
          {/* NAME */}
          <div className="form-group">
            <label className="form-label required">{t('admin.user_management.user_name')}</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* ROLE */}
          <div className="form-group">
            <label className="form-label required">{t('admin.user_management.role')}</label>
            <select
              name="role"
              className="form-select"
              value={formData.role}
              onChange={handleInputChange}
              required
            >
              <option value="">{t('admin.user_management.select_role')}</option>
              <option value="ADMIN">ADMIN</option>
              <option value="USER">USER</option>
            </select>
          </div>

          {/* EMAIL */}
          <div className="form-group">
            <label className="form-label required">{t('admin.user_management.email')}</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* PHONE */}
          <div className="form-group">
            <label className="form-label required">{t('admin.user_management.phone')}</label>
            <input
              type="tel"
              name="phone"
              className="form-input"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* PASSWORD + CONFIRM */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label required">{t('admin.user_management.password')}</label>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">{t('admin.user_management.confirm_password')}</label>
              <div className="password-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="form-input"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                </button>
              </div>
            </div>
          </div>

          {/* STATUS TOGGLE */}
          <div className="form-group">
            <div className="status-toggle">
              <span className="form-label">{t('admin.user_management.status')}</span>
              <div
                className="toggle-switch"
                onClick={() =>
                  setFormData(prev => ({ ...prev, status: !prev.status }))
                }
                style={{
                  width: '40px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: formData.status ? '#16a34a' : '#d1d5db',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <div
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    transform: formData.status
                      ? 'translateX(20px)'
                      : 'translateX(0)',
                    transition: 'transform 0.3s ease'
                  }}
                ></div>
              </div>
              <span style={{ marginLeft: 10 }}>
                {formData.status ? t('status.user.active') : t('status.user.inactive')}
              </span>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              {t('admin.user_management.actions.cancel')}
            </button>
            <button type="submit" className="submit-btn">
              {t('admin.user_management.actions.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
